<template>
  <div>
    <AccordionCard :state="cards.cableManagementC2State">
      <template v-slot:header>
        {{ $t('cableManagementC2State') }}
      </template>

      <div class="flex-row">
        <div class="flex-column name">
          {{ $t('cableManagementC2State') }}
        </div>
        <div class="flex-column select">
          <SelectButton
            :disabled="isBlockReadOnly"
            v-model="project.serviceReport.cableManagementC2State"
            :options="getEnumValues('YesNo')"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>
        </div>
      </div>
    </AccordionCard>

    <AccordionCard :state="cards.serviceCableManagementAttachments">
      <template v-slot:header>
        {{ $t('cableManagementAttachments') }}
      </template>

      <div class="flex-row">
        <div class="flex-column name">{{ $t('cableManagementAttachments') }}</div>
        <div class="flex-column select">
          <div>
            {{ $t('templateAttachments') }}:
            <AttachmentGallery
              :attachments="referenceData.reportTemplateImages[2].templateAttachments"
              :deletable="false"
              :sortByName="false"
              :showFilename="false"
            ></AttachmentGallery>
          </div>
        </div>
      </div>

      <div class="flex-row">
        <div class="flex-column name"></div>
        <div class="flex-column select">
          <AttachmentGallery
            :attachments="project.serviceCableManagementAttachments"
            attachmentType="serviceCableManagementAttachments"
            :deletable="!isBlockReadOnly"
            :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
            v-on:attachment-list:delete="onDeleteProjectAttachment"
            :sortByName="false"
          ></AttachmentGallery>
          <DropzoneUpload
            v-if="!isBlockReadOnly"
            class="mt-2"
            :complete="iscableManagementAttachmentsUploadComplete"
            :parentId="project.id"
            parentType="serviceCableManagementAttachments"
            :uploadCallback="onUploadProjectAttachment"
            v-on:dropzone-upload:reset="iscableManagementAttachmentsUploadComplete = false"
            acceptedFileTypes="image/*"
          />
        </div>
      </div>
    </AccordionCard>

    <AccordionCard :state="cards.serviceCableAlongFacadeAttachments">
      <template v-slot:header>
        {{ $t('cableAlongFacadeAttachments') }}
      </template>

      <div class="flex-row">
        <div class="flex-column name">{{ $t('cableAlongFacadeAttachments') }}</div>
        <div class="flex-column select">
          <div>
            {{ $t('templateAttachments') }}:
            <AttachmentGallery
              :attachments="referenceData.reportTemplateImages[44].templateAttachments"
              :deletable="false"
              :sortByName="false"
              :showFilename="false"
            ></AttachmentGallery>
          </div>
        </div>
      </div>

      <div class="flex-row">
        <div class="flex-column name">{{ $t('cableAlongFacadeAttachments') }}</div>
        <div class="flex-column select">
          <AttachmentGallery
            :attachments="project.serviceCableAlongFacadeAttachments"
            attachmentType="serviceCableAlongFacadeAttachments"
            :deletable="!isBlockReadOnly"
            :hidesDeleteForGuests="!isOrganizationRoleNavigationEnabled"
            v-on:attachment-list:delete="onDeleteProjectAttachment"
            :sortByName="false"
          ></AttachmentGallery>
          <DropzoneUpload
            v-if="!isBlockReadOnly"
            class="mt-2"
            :complete="iscableAlongFacadeAttachmentsUploadComplete"
            :parentId="project.id"
            parentType="serviceCableAlongFacadeAttachments"
            :uploadCallback="onUploadProjectAttachment"
            v-on:dropzone-upload:reset="iscableAlongFacadeAttachmentsUploadComplete = false"
            acceptedFileTypes="image/*"
          />
        </div>
      </div>
    </AccordionCard>

    <AccordionCardOptionalInput
      :card="cards.cableManagementOptional"
      :project="project"
      reportPath="serviceReport"
      notesType="cableManagementOptionalNotes"
      :attachments="project.serviceCableManagementOptionalAttachments"
      attachmentType="serviceCableManagementOptionalAttachments"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import AccordionCard from '@/components/AccordionCard';
import AccordionCardOptionalInput from '@/components/AccordionCardOptionalInput';
import AttachmentGallery from '@/components/AttachmentGallery';
import DropzoneUpload from '@/components/DropzoneUpload';
import SelectButton from 'primevue/selectbutton';

export default {
  name: 'ExistingDamage',
  components: {
    AccordionCard,
    AttachmentGallery,
    DropzoneUpload,
    AccordionCardOptionalInput,
    SelectButton,
  },
  mixins: [AccessBlockMixin],
  props: {
    project: { type: Object },
    cards: { type: Object },
    /**
     * Template images from `MobileProjectsServiceReport` (block field, or the store getter outside
     * the block).
     */
    referenceData: { type: Object, default: () => ({ reportTemplateImages: [] }) },
  },
  data() {
    return {
      /** track Upload states for clearing dropzone preview */
      iscableManagementAttachmentsUploadComplete: false,
      iscableAlongFacadeAttachmentsUploadComplete: false,
      iscableBetweenMateboxAndElectricMeterAttachmentsUploadComplete: false,
    };
  },
  computed: {
    ...mapGetters(['getEnumValues']),
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    isPitchedRoof() {
      return this.project.measurement && this.project.measurement.roofType === 'PITCHED_ROOF';
    },
  },

  methods: {
    ...mapActions(['uploadProjectAttachment', 'deleteProjectAttachment']),

    async onUploadProjectAttachment(payload) {
      await this.uploadProjectAttachment(payload);
    },

    async onDeleteProjectAttachment(event) {
      await this.deleteProjectAttachment({
        projectId: this.project.id,
        attachmentId: event.attachmentId,
        attachmentType: event.attachmentType,
      });
    },
  },
  async mounted() {
    // after upload is complete emit event to clear dropzone preview
    this.$store.subscribeAction({
      after: (action, state) => {
        if (action.type === 'uploadProjectAttachment') {
          this.iscableManagementAttachmentsUploadComplete = true;
          this.iscableAlongFacadeAttachmentsUploadComplete = true;
          this.iscableBetweenMateboxAndElectricMeterAttachmentsUploadComplete = true;
        }
      },
    });
  },
};
</script>

<style lang="scss" scoped></style>
